import React from "react";

import { css } from "@emotion/core";

import { Margin, Text, TypographyName } from "@zapier/design-system";

import {
  Currency,
  RoundingStrategy,
  defaultCurrency,
  formatPriceToBeInCurrency,
  getDecimalSeparator,
} from "@zapier/currency";

type FancyPriceProps = {
  /** Price in USD pennies (i.e. hundredths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
  /**
   * For currencies that don't have decimals/fractional units (e.g. JPY), what type of rounding strategy should we use?
   * For example:
   *   - When showing monthly prices for annual plans, we should round UP in favor of the user (price * 12 will be less than what the user actually pays)
   *   - When showing standard monthly prices, use standard rounding practices i.e. round UP for >= .50 and DOWN for < .50
   */
  roundingStrategy?: RoundingStrategy;
  /** Sets smaller typography taking less space as a whole */
  isSmall?: boolean;
};

const root = css`
  display: flex;
  margin: 0px;
`;

// Get typography used by elements based on isSmall prop flag
const getTypography = (isSmall = false): { [key: string]: TypographyName } => {
  return isSmall
    ? {
        currency: "smallPrint1Semibold",
        decimal: "smallPrint1Bold",
        price: "pageHeader7",
        symbol: "smallPrint1Semibold",
      }
    : {
        currency: "paragraph1",
        decimal: "paragraph1Bold",
        price: "pageHeader5",
        symbol: "sectionHeader3Semibold",
      };
};

/**
 * Displays a price in a styled and user-friendly format.
 */
export const FancyPrice = ({
  children,
  roundingStrategy,
  currency = defaultCurrency,
  isSmall = false,
}: FancyPriceProps) => {
  const price = formatPriceToBeInCurrency(children, currency, false, {
    maxDecimals: 2,
    minDecimals: 0,
    roundingStrategy: currency?.zeroDecimal ? roundingStrategy : undefined,
    useRelativeRounding: false,
  });
  const separator = getDecimalSeparator();
  const tokens = price.split(separator);
  const typography = getTypography(isSmall);

  return (
    <div css={root}>
      <Margin margin={isSmall ? "7px 0 0 0" : "10px 0 0 0"}>
        <Text color="neutral800" type={typography.symbol}>
          {currency.symbol}
        </Text>
      </Margin>

      <Text color="neutral800" type={typography.price}>
        {tokens[0]}
      </Text>

      {tokens.length > 1 && (
        <Margin margin={isSmall ? "7px 0 0 2px" : "6px 0 0 2px"}>
          <Text color="neutral800" type={typography.decimal}>
            {separator}
            {tokens[1]}
          </Text>
        </Margin>
      )}

      {currency.symbol === "$" && (
        <Margin margin={isSmall ? "7px 0 0 1px" : "6px 0 0 5px"}>
          <Text color="neutral800" type={typography.currency}>
            {currency.name}
          </Text>
        </Margin>
      )}
    </div>
  );
};
